import React, { useState, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getMostRecentLocations } from 'emergency_data/src/utils/getMostRecentLoc';
import PropTypes from 'prop-types';
import { withTheme } from 'styled-components';
import { XCloseIcon } from '@rsos/assets/icons';
import SplitPane from '@rsos/components/capstone/DataCards/SplitPane';
import { Box } from '@rsos/components/capstone/base';
import { LayoutContext } from '@rsos/components/capstone/base/Layout';
import { BP_LAPTOP } from '@rsos/constants/breakpoints';
import { formatPhoneWithDashes } from '@rsos/utils';
import useIsIRP from '@rsos/utils/useIsIRP';
import useIsTF911Enabled from '../../../hooks/useIsTF911Enabled';
import useShouldRenderTF911 from '../../../hooks/useShouldRenderTF911';
import ADRContainer from '../../AdditionalData/ADRContainer';
import AgencyShareChatButton from '../../AgencyShareChat/AgencyShareChatButton';
import CopyAllButton from '../../CopyAllButton';
import CallSource from '../../Location/CallSource';
import { getDataSource } from '../../Location/helpers';
import LocationList from '../../LocationList';
import OnsitePersonnelBanner from '../../SpatialLayers/OnsitePersonnelBanner';
import What3WordsSearchResult from '../../What3Words/What3WordsSearchResult';
import { CHAT_HEADER_HEIGHT } from '../CallChats/AgencyShareChat/ASCChatBox.styles';
import CallChats, {
  CHAT_SOURCE_ASC,
  CHAT_SOURCE_TF911,
} from '../CallChats/CallChats';
import { TF911_CHAT_HEADER_HEIGHT } from '../CallChats/TF911ChatBox/TF911ChatBox';
import BPLaptopHeader from './BPLaptopHeader';
import {
  PANE_HEADER_HEIGHT,
  StyledCallPane,
  StyledHeader,
  StyledCallerID,
  CloseButton,
  Tools,
} from './CallPane.styles';

const CallPane = withTheme(
  ({ height, onActionableButton, isInternational, onClose, theme, hasW3w }) => {
    const { layout } = useContext(LayoutContext);

    const [accordionHeight, setAccordionHeight] = useState(0);

    const callerID = useSelector(
      state => state.emergencyCalls.selectedCallerID,
    );

    const currentCall = useSelector(
      state => state.emergencyCalls.callQueue[callerID],
    );

    const isCallShared = useSelector(
      state =>
        state.emergencyCalls.callQueue[callerID]?.agencyShareChat?.isCallShared,
    );

    const isCallReceived = useSelector(
      state =>
        state.emergencyCalls.callQueue[callerID]?.agencyShareChat
          ?.isCallReceived,
    );

    const formattedPhone = formatPhoneWithDashes(callerID);

    const {
      mostRecentLocation,
      mostRecentSecondaryLocation,
    } = getMostRecentLocations(currentCall);

    const source = getDataSource(
      mostRecentLocation || mostRecentSecondaryLocation,
    );

    const isAllCallsSelected = useSelector(
      state => !state.emergencyCalls.selectedCallerID,
    );

    const { isIRPresponsiveConditional } = useIsIRP();

    const displayPhoneNumber = isInternational ? callerID : formattedPhone;

    const availableHeight = height - PANE_HEADER_HEIGHT - accordionHeight;

    const isTF911Enabled = useIsTF911Enabled();
    const shouldRenderTF911 = useShouldRenderTF911(callerID);

    const isAnyChatEnabled = useMemo(() => {
      return (
        isCallReceived || isCallShared || (isTF911Enabled && shouldRenderTF911)
      );
    }, [isCallReceived, isCallShared, isTF911Enabled, shouldRenderTF911]);

    const maxChatsCount = useMemo(() => {
      let count = 0;
      if (isTF911Enabled && shouldRenderTF911) {
        count += 1;
      }
      if (isCallReceived || isCallShared) {
        count += 1;
      }
      return count;
    }, [isCallReceived, isCallShared, isTF911Enabled, shouldRenderTF911]);

    const [
      isAgencyShareChatChatBoxVisible,
      setIsAgencyShareChatChatBoxVisible,
    ] = useState(true);

    const [
      isTextFrom911ChatBoxVisible,
      setIsTextFrom911ChatBoxVisible,
    ] = useState(true);

    const [prevChatCollapsed, setPrevChatCollapsed] = useState('');

    const [collapsedChatsCount, setCollapsedChatsCount] = useState(0);

    /**
     * Callback function to handle the SplitPane height. Lets the Pane component
     * know which chat is collapsed.
     * @param {String} chatSource - 'asc' or 'tf911
     */
    const handleCollapseChat = chatSource => {
      if (chatSource === CHAT_SOURCE_ASC) {
        if (isAgencyShareChatChatBoxVisible) {
          setCollapsedChatsCount(prevCount => prevCount + 1);
          setPrevChatCollapsed(CHAT_SOURCE_ASC);
        } else {
          setCollapsedChatsCount(prevCount => prevCount - 1);
        }

        setIsAgencyShareChatChatBoxVisible(prevIsVisible => !prevIsVisible);
      } else if (chatSource === CHAT_SOURCE_TF911) {
        if (isTextFrom911ChatBoxVisible) {
          setCollapsedChatsCount(prevCount => prevCount + 1);
          setPrevChatCollapsed(CHAT_SOURCE_TF911);
        } else {
          setCollapsedChatsCount(prevCount => prevCount - 1);
        }

        setIsTextFrom911ChatBoxVisible(prevIsVisible => !prevIsVisible);
      }
    };

    const handleOuterPrimaryPaneChange = change => {
      if (collapsedChatsCount === maxChatsCount) {
        // Show the last collapsed chat
        if (prevChatCollapsed === CHAT_SOURCE_ASC) {
          setIsAgencyShareChatChatBoxVisible(prev => !prev);
        } else if (prevChatCollapsed === CHAT_SOURCE_TF911) {
          setIsTextFrom911ChatBoxVisible(prev => !prev);
        }

        if (prevChatCollapsed !== '') {
          setPrevChatCollapsed('');
          setCollapsedChatsCount(prevCount => prevCount - 1);
        }
      }
    };

    const p2MaxHeight = useMemo(() => {
      if (collapsedChatsCount === 0) {
        return availableHeight;
      } else if (collapsedChatsCount === maxChatsCount) {
        if (!isAgencyShareChatChatBoxVisible && !isTextFrom911ChatBoxVisible) {
          return `${CHAT_HEADER_HEIGHT + TF911_CHAT_HEADER_HEIGHT + 10}px`;
        } else if (!isAgencyShareChatChatBoxVisible) {
          return `${CHAT_HEADER_HEIGHT}px`;
        } else if (!isTextFrom911ChatBoxVisible) {
          return `${TF911_CHAT_HEADER_HEIGHT}px`;
        }
      } else {
        if (isAgencyShareChatChatBoxVisible) {
          return CHAT_HEADER_HEIGHT + availableHeight / 2;
        } else if (isTextFrom911ChatBoxVisible) {
          return TF911_CHAT_HEADER_HEIGHT + availableHeight / 2;
        }
        return availableHeight;
      }
    }, [
      availableHeight,
      collapsedChatsCount,
      isAgencyShareChatChatBoxVisible,
      isTextFrom911ChatBoxVisible,
      maxChatsCount,
    ]);

    return (
      <StyledCallPane data-name="event-pane">
        {hasW3w && (
          <What3WordsSearchResult
            isAllCallsSelected={isAllCallsSelected}
            hasW3w={hasW3w}
            setAccordionHeight={setAccordionHeight}
            accordionHeight={accordionHeight}
          />
        )}
        {layout?.window?.width <= BP_LAPTOP && !isIRPresponsiveConditional ? (
          <BPLaptopHeader
            onClose={onClose}
            formattedPhone={displayPhoneNumber}
            source={source}
          />
        ) : (
          <StyledHeader data-name="event-pane-header">
            <Box align="center" horizontal justify="space-between">
              <StyledCallerID data-name="event-pane-caller-id">
                {displayPhoneNumber}
              </StyledCallerID>
              <Tools align="center" horizontal data-name="event-pane-tools">
                <AgencyShareChatButton />
                <CopyAllButton />
                <CloseButton onClick={() => onClose()}>
                  <XCloseIcon />
                </CloseButton>
              </Tools>
            </Box>
            <Box>
              <CallSource source={source} />
            </Box>
          </StyledHeader>
        )}
        <SplitPane
          disabled={!isAnyChatEnabled}
          paneStyle={{
            background: `${theme.colors.background}`,
            borderRadius: '5px',
            overflowY: 'hidden',
            position: 'relative',
          }}
          pane2Style={{
            maxHeight: p2MaxHeight,
          }}
          primary="second"
          height={availableHeight}
          onChange={handleOuterPrimaryPaneChange}
        >
          <SplitPane
            height={availableHeight}
            disabled
            // moved Historic Locations to a dropdown and Resizer is no longer needed
            primary="second"
            paneStyle={{
              border: `1px solid ${theme.colors.lightLineDivider}`,
              overflowY: 'auto',
              position: 'relative',
            }}
          >
            <LocationList />
            <OnsitePersonnelBanner />
            <ADRContainer onActionableButton={onActionableButton} />
          </SplitPane>

          {isAnyChatEnabled && (
            <CallChats
              isAgencyShareChatChatBoxVisible={isAgencyShareChatChatBoxVisible}
              isTextFrom911ChatBoxVisible={isTextFrom911ChatBoxVisible}
              onCollapseChat={handleCollapseChat}
            />
          )}
        </SplitPane>
      </StyledCallPane>
    );
  },
);

CallPane.propTypes = {
  height: PropTypes.number.isRequired,
  onActionableButton: PropTypes.func,
  onClose: PropTypes.func,
  isInternational: PropTypes.bool,
};

CallPane.defaultProps = {
  onActionableButton: () => {},
  onClose: () => {},
};

export default CallPane;
